import CommonSearch from "@/components/CommonUi/CommonSearch";
import Filter from "@/components/CommonUi/Filter";
import PrintPDF from "@/components/CommonUi/PrintPdf";
import ColVisibilityDropdown from "@/components/Shared/ColVisibilityDropdown";
import {
  useGetUserColumnSettingsQuery,
  useSaveUserColumnSettingsMutation,
  useResetUserColumnSettingsMutation,
} from "@/redux/rtk/features/userColumnSettings/userColumnSettingsApi";
import CSV from "@/UI/CSV";
import Pagination from "@/UI/Pagination";
import Segmented from "@/UI/Segmented";
import Table from "@/UI/Table";
import { Button, Popover } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { BsDatabaseExclamation, BsFiletypeCsv } from "react-icons/bs";
import { CiCreditCard2, CiViewTable } from "react-icons/ci";
import { FiMoreVertical, FiPrinter } from "react-icons/fi";
import ResponsiveMobileCard from "./ResponsiveMobileCard";
import React from "react";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";

export default function TableComponent({
  list,
  total,
  loading,
  children,
  filters,
  columns,
  extraFilter,
  title,
  setPageConfig,
  isSearch,
  loadingUiSize = 10,
  nestedRowKey,
  card,
  defaultView = "list",
  tabs = [],
  primaryActions = [],
  secondaryActions = [],
  componentTitle,
  extra,
  rowSelection,
  onRow,
  pageConfig,
  exportPermission,
  printPermission,
  columnKey,
}) {
  const onSort = ({ key, direction }) => {
    if (setPageConfig) {
      setPageConfig((prev) => {
        return { ...prev, sortBy: key, sortOrder: direction, page: 1 };
      });
    }
  };

  const fetchData = (page, count) => {
    setPageConfig((prev) => {
      return { ...prev, page, count };
    });
  };

  const { t, i18n } = useTranslation();
  const [view, setView] = useState(defaultView);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [activeTab, setActiveTab] = useState(tabs[0]?.value || "all");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const lastColKeysRef = useRef("");
  const lastLangRef = useRef(i18n.language);
  const isSavingRef = useRef(false);
  const getColId = (col) => col.key || col.dataIndex || col.title;

  const tableKey = columnKey || `${window.location.pathname}_${columns.map(getColId).filter(Boolean).join('_')}`.replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 200);

  const { data: savedSettings, isSuccess: isSettingsSuccess, isError: isSettingsError } = useGetUserColumnSettingsQuery(tableKey, {
    skip: !tableKey
  });
  const [saveSettings] = useSaveUserColumnSettingsMutation();
  const [resetSettings] = useResetUserColumnSettingsMutation();

  // True once the API has responded (either with data or 404)
  const isSettingsLoaded = isSettingsSuccess || isSettingsError;
  const settingsAppliedRef = useRef(false);

  useEffect(() => {
    if (isSavingRef.current) {
      isSavingRef.current = false;
      return;
    }

    if (savedSettings?.columns && savedSettings.columns.length > 0) {
      const configMap = new Map(savedSettings.columns.map(c => [c.id, c]));

      const reconstructed = [];
      savedSettings.columns.forEach(savedCol => {
        const matchingProp = columns.find(c => getColId(c) === savedCol.id);
        if (matchingProp) {
          reconstructed.push({
            ...matchingProp,
            visible: savedCol.visible,
            width: savedCol.width ?? matchingProp.width
          });
        }
      });

      columns.forEach(propCol => {
        const propId = getColId(propCol);
        if (!configMap.has(propId)) {
          reconstructed.push({
            ...propCol,
            visible: true
          });
        }
      });

      settingsAppliedRef.current = true;
      setColumnsToShow(reconstructed);
    } else if (isSettingsLoaded) {
      // Only fall back to defaults AFTER API has confirmed no saved settings exist
      // and only if we haven't already applied saved settings this session
      if (!settingsAppliedRef.current) {
        const currentPropKeys = columns.map(getColId).join(",");
        if (columnsToShow.length === 0 || currentPropKeys !== lastColKeysRef.current) {
          lastColKeysRef.current = currentPropKeys;

          const merged = columns.map(newCol => {
            const existing = columnsToShow.find(c => getColId(c) === getColId(newCol));
            return existing ? { ...newCol, width: existing.width, visible: existing.visible ?? true } : { ...newCol, visible: true };
          });

          const ordered = [];
          columnsToShow.forEach(c => {
            const found = merged.find(m => getColId(m) === getColId(c));
            if (found) ordered.push(found);
          });
          merged.forEach(m => {
            if (!ordered.some(o => getColId(o) === getColId(m))) ordered.push(m);
          });

          setColumnsToShow(ordered.length > 0 ? ordered : columns.map(c => ({ ...c, visible: true })));
        }
      }
    }
  }, [columns, savedSettings, isSettingsLoaded]);

  useEffect(() => {
    if (i18n.language !== lastLangRef.current) {
      lastLangRef.current = i18n.language;
      setColumnsToShow(prev => prev.map(existing => {
        const matchingProp = columns.find(c => getColId(c) === getColId(existing));
        return matchingProp ? { ...existing, title: matchingProp.title } : existing;
      }));
    }
  }, [i18n.language, columns]);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const columnsToShowHandler = async (val) => {
    setColumnsToShow(val);
    const columnsToSave = val.map(col => ({
      id: getColId(col),
      visible: col.visible ?? true,
      width: col.width
    }));
    try {
      isSavingRef.current = true;
      await saveSettings({ tableKey, columns: columnsToSave }).unwrap();
    } catch (err) {
      console.error("Failed to save column settings:", err);
      isSavingRef.current = false;
    }
  };

  const handleResetColumns = async () => {
    try {
      isSavingRef.current = true;
      await resetSettings(tableKey).unwrap();
      setColumnsToShow(columns.map(c => ({ ...c, visible: true })));
    } catch (err) {
      console.error("Failed to reset column settings:", err);
      isSavingRef.current = false;
    }
  };

  const handleChange = (value) => {
    setView(value);
  };

  const options = [
    {
      value: "card",
      icon: <CiCreditCard2 size={20} />,
      title: t("common.card") || "Card",
    },
    {
      value: "list",
      icon: <CiViewTable size={20} />,
      title: t("common.list") || "List",
    },
  ];

  // Mobile popover content
  const mobilePopoverContent = (
    <div className="flex flex-col gap-1 py-1 min-w-[160px]">
      <UserPrivateComponent permission={printPermission}>
        <div className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors">
          <FiPrinter className="text-gray-500 text-sm" />
          <PrintPDF list={list} columns={columns} title={title} />
        </div>
      </UserPrivateComponent>

      <UserPrivateComponent permission={exportPermission}>
        <div className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors">
          <BsFiletypeCsv className="text-gray-500 text-sm" />
          <CSV notButton={true} list={list} columns={columns} title={title} />
        </div>
      </UserPrivateComponent>

      {!card && !isMobile && (
        <>
          <div className="h-px bg-gray-200 my-1" />
          <div className="px-2 py-2 hover:bg-gray-50 rounded-md transition-colors">
            <ColVisibilityDropdown
              options={columns}
              columns={columnsToShow}
              columnsToShowHandler={columnsToShowHandler}
              onReset={handleResetColumns}
            />
          </div>
        </>
      )}
    </div>
  );

  // Render loading skeleton for cards
  const renderCardSkeleton = () => {
    return Array(loadingUiSize)
      .fill(null)
      .map((_, index) => (
        <div
          key={index}
          className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100">
            <div className="flex-1">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
            <div className="w-8 h-8 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex justify-between">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      ));
  };

  // Determine if we should show mobile card view
  const shouldShowMobileCards = isMobile || (card && view === "card");

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Top Navigation Bar */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center justify-between px-4 py-2 gap-2">
          <div className="flex items-center gap-4">
            <h1 className="text-lg">{componentTitle}</h1>
            <div className="cartExtra flex gap-2 items-center">{extra}</div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hideScrollBar overflow-x-auto overflow-y-hidden flex items-center gap-2">
              {extraFilter && React.cloneElement(extraFilter, { mode: "savedOnly" })}

              {!card && !isMobile && (
                <div>
                  <ColVisibilityDropdown
                    options={columns}
                    columns={columnsToShow}
                    columnsToShowHandler={columnsToShowHandler}
                    onReset={handleResetColumns}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters & View Controls Bar */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        {/* Mobile Layout */}
        <div className="md:hidden space-y-3">
          {isSearch && (
            <div className="flex-1 max-w-full">
              <CommonSearch
                setPageConfig={setPageConfig}
                placeholder={t("common.search")}
              />
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="hideScrollBar overflow-x-auto overflow-y-hidden flex items-center gap-2 flex-1">
            </div>

            <div className="flex items-center gap-2">
              {card && (
                <Segmented
                  options={options}
                  value={view}
                  onChange={handleChange}
                />
              )}

              <Popover
                content={mobilePopoverContent}
                trigger="click"
                placement="bottomLeft"
                open={popoverOpen}
                onOpenChange={setPopoverOpen}
                overlayClassName="mobile-popover">
                <Button
                  type="text"
                  size="small"
                  className="h-8 w-8 p-0 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  icon={<FiMoreVertical className="text-gray-600" />}
                />
              </Popover>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            {isSearch && (
              <div className="w-64 max-w-full">
                <CommonSearch
                  setPageConfig={setPageConfig}
                  placeholder={t("common.search")}
                />
              </div>
            )}

            <div className="hideScrollBar overflow-x-auto overflow-y-hidden flex-1 flex items-center gap-2">
              <Filter
                setPageConfig={setPageConfig}
                filters={filters}
                extraFilter={extraFilter && React.cloneElement(extraFilter, { mode: "filterOnly" })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {card && (
              <Segmented
                options={options}
                value={view}
                onChange={handleChange}
              />
            )}

            <div className="flex items-center gap-2">
              <UserPrivateComponent permission={printPermission}>
                <Button color="white" className="text-xs flex items-center gap-1">
                  <FiPrinter />
                  <PrintPDF list={list} columns={columns} title={title} />
                </Button>
              </UserPrivateComponent>

              <UserPrivateComponent permission={exportPermission}>
                <Button color="white" className="text-xs flex items-center gap-1">
                  <BsFiletypeCsv />
                  <CSV
                    notButton={true}
                    list={list}
                    columns={columns}
                    title={title}
                  />
                </Button>
              </UserPrivateComponent>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="">
        {/* Desktop Table View or when list view is selected */}
        {!shouldShowMobileCards && (
          <Table
            key={i18n.language}
            loading={loading}
            columns={columnsToShow.filter(c => c.visible !== false)}
            nestedRowKey={nestedRowKey}
            data={
              !!list?.length && list.map((item) => ({ ...item, key: item?.id }))
            }
            scroll={{ y: window.innerHeight - 250 }}
            loadingUiSize={loadingUiSize}
            showSearch={isSearch}
            showViewToggle={!!card}
            componentTitle={componentTitle}
            rowSelection={rowSelection}
            onRow={onRow}
            sortConfig={{
              key: pageConfig?.sortBy,
              direction: pageConfig?.sortOrder,
            }}
            onSort={onSort}
          />
        )}

        {/* Mobile Card View or when card view is selected */}
        {shouldShowMobileCards && (
          <div className="p-4">
            {loading && !list?.length ? (
              <div className="grid grid-cols-1 gap-4">
                {renderCardSkeleton()}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {list?.map((item) => (
                    <ResponsiveMobileCard
                      key={item.id}
                      item={item}
                      columns={columns}
                    />
                  ))}
                </div>
                {!list?.length && !loading && (
                  <div className="flex flex-col justify-center items-center py-16">
                    <BsDatabaseExclamation
                      className="text-gray-300 mb-3"
                      size={60}
                    />
                    <span className="text-gray-500">{t("common.empty")}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Custom Card View (if card prop is provided) */}
        {card && view === "card" && !isMobile && (
          <div className="p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {list?.map((item) => card(item))}
            </div>
            {!list?.length && !loading && (
              <div className="flex flex-col justify-center items-center py-16">
                <BsDatabaseExclamation
                  className="text-gray-300 mb-3"
                  size={60}
                />
                <span className="text-gray-500">{t("common.empty")}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      {total >= 11 && (
        <div className="flex items-center justify-center px-4 py-3 border-t border-gray-200 dark:border-gray-700 space-x-4">
          <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.total')}: {total}</span>
          <Pagination onChange={fetchData} total={total} />
        </div>
      )}

      {children && children}
    </div>
  );
}
